import * as XLSX from 'xlsx';
import { toast } from 'react-hot-toast';

// Get available sheets from Excel file
export const getExcelSheets = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        
        const sheets = workbook.SheetNames.map(sheetName => {
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
          
          // Count non-empty rows
          const nonEmptyRows = jsonData.filter(row => 
            row.some(cell => cell && String(cell).trim() !== '')
          ).length;
          
          return {
            name: sheetName,
            rowCount: nonEmptyRows
          };
        });
        
        resolve(sheets);
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
};

// Common utility functions for import functionality
export const readExcelFile = (file, sheetName = null) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { 
          type: 'array',
          cellDates: false, // Disable automatic date parsing to handle manually
          cellNF: false,    // Disable number format
          cellText: false,  // Use raw values
          raw: true         // Get raw cell values
        });
        
        // Get the specified sheet or first sheet
        const targetSheetName = sheetName || workbook.SheetNames[0];
        const worksheet = workbook.Sheets[targetSheetName];
        
        if (!worksheet) {
          throw new Error(`Sheet "${targetSheetName}" not found`);
        }
        
        // Convert to JSON with better options
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
          header: 1, 
          defval: '',
          raw: true, // Keep raw values for better date handling
          blankrows: false
        });
        
        // Find header row (look for row with most non-empty cells)
        let headerRowIndex = 0;
        let maxNonEmptyCells = 0;
        
        for (let i = 0; i < Math.min(jsonData.length, 10); i++) {
          const row = jsonData[i];
          const nonEmptyCells = row.filter(cell => cell && String(cell).trim() !== '').length;
          if (nonEmptyCells > maxNonEmptyCells) {
            maxNonEmptyCells = nonEmptyCells;
            headerRowIndex = i;
          }
        }
        
        // Extract headers and data
        const headers = jsonData[headerRowIndex] || [];
        const dataRows = jsonData.slice(headerRowIndex + 1).filter(row => 
          row.some(cell => cell && String(cell).trim() !== '')
        );
        
        // Convert to objects with smart value processing
        const result = dataRows.map(row => {
          const obj = {};
          headers.forEach((header, index) => {
            if (header) {
              let value = row[index] || '';
              const headerStr = String(header).trim().toLowerCase();
              
              // Check if this column is likely a date column
              const isDateColumn = headerStr.includes('date') || 
                                 headerStr.includes('time') || 
                                 headerStr.includes('day') ||
                                 headerStr.includes('created') ||
                                 headerStr.includes('due');
              
              // If it's a date column, try to parse the date
              if (isDateColumn && value) {
                console.log(`Processing date column "${header}":`, { original: value, type: typeof value });
                const parsedDate = parseDateValue(value);
                if (parsedDate) {
                  console.log(`Date parsed successfully:`, { original: value, parsed: parsedDate });
                  value = parsedDate;
                } else {
                  console.log(`Date parsing failed for:`, value);
                }
              }
              
              obj[String(header).trim()] = value;
            }
          });
          return obj;
        });
        
        resolve({
          data: result,
          sheetName: targetSheetName,
          totalSheets: workbook.SheetNames.length
        });
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
};

// Auto-mapping function for common field patterns
export const generateAutoMappings = (headers, fieldDefinitions) => {
  const autoMappings = {};
  
  headers.forEach(header => {
    const lowerHeader = header.toLowerCase();
    fieldDefinitions.forEach(field => {
      if (!autoMappings[field.key] && field.autoMapPatterns) {
        const isMatch = field.autoMapPatterns.some(pattern => 
          lowerHeader.includes(pattern.toLowerCase())
        );
        if (isMatch) {
          autoMappings[field.key] = header;
        }
      }
    });
  });
  
  return autoMappings;
};

// Validate required fields
export const validateRequiredFields = (data, fieldDefinitions) => {
  const errors = [];
  
  data.forEach((item, index) => {
    fieldDefinitions.forEach(field => {
      if (field.required && (!item[field.key] || String(item[field.key]).trim() === '')) {
        errors.push(`Row ${index + 1}: ${field.label} is required`);
      }
    });
  });
  
  return errors;
};

// Parse numeric values
export const parseNumericValue = (value, defaultValue = 0) => {
  if (value === null || value === undefined || value === '') return defaultValue;
  
  const originalValue = value;
  const cleanedValue = String(value).replace(/[^\d.-]/g, '');
  const parsed = parseFloat(cleanedValue);
  
  // Debug logging for rate values specifically
  if (String(originalValue).toLowerCase().includes('rate') || 
      (typeof originalValue === 'number' && originalValue > 0)) {
    console.log('parseNumericValue debug:', {
      original: originalValue,
      cleaned: cleanedValue,
      parsed: parsed,
      isNaN: isNaN(parsed),
      result: isNaN(parsed) ? defaultValue : parsed
    });
  }
  
  return isNaN(parsed) ? defaultValue : parsed;
};


// Parse date values - Fresh implementation with better DD-MM-YY support
export const parseDateValue = (value) => {
  if (!value) return null;
  
  const dateStr = String(value).trim();
  
  // Debug logging
  console.log('Parsing date value:', { original: value, string: dateStr, type: typeof value });
  
  // 1. Try Excel serial number first (most common issue)
  const serialNumber = parseFloat(dateStr);
  if (!isNaN(serialNumber) && serialNumber > 25000 && serialNumber < 100000) {
    const excelEpoch = new Date(1900, 0, 1);
    const days = serialNumber - 2; // Excel leap year bug
    const excelDate = new Date(excelEpoch.getTime() + days * 24 * 60 * 60 * 1000);
    
    if (!isNaN(excelDate.getTime()) && 
        excelDate.getFullYear() >= 1970 && 
        excelDate.getFullYear() <= 2100) {
      console.log('Excel serial date converted:', { serial: serialNumber, date: excelDate.toISOString().split('T')[0] });
      return excelDate.toISOString().split('T')[0];
    }
  }
  
  // 2. Try direct Date parsing (handles most standard formats)
  const directDate = new Date(dateStr);
  if (!isNaN(directDate.getTime()) && 
      directDate.getFullYear() >= 1900 && 
      directDate.getFullYear() <= 2100) {
    console.log('Direct date parsing successful:', { input: dateStr, result: directDate.toISOString().split('T')[0] });
    return directDate.toISOString().split('T')[0];
  }
  
  // 3. Try manual format parsing with priority for DD-MM-YY
  const formats = [
    // DD-MM-YY (2 digit year) - HIGHEST PRIORITY
    { pattern: /^(\d{1,2})-(\d{1,2})-(\d{2})$/, day: 1, month: 2, year: 3, twoDigit: true, name: 'DD-MM-YY' },
    // DD/MM/YY (2 digit year)
    { pattern: /^(\d{1,2})\/(\d{1,2})\/(\d{2})$/, day: 1, month: 2, year: 3, twoDigit: true, name: 'DD/MM/YY' },
    // DD-MM-YYYY (4 digit year)
    { pattern: /^(\d{1,2})-(\d{1,2})-(\d{4})$/, day: 1, month: 2, year: 3, twoDigit: false, name: 'DD-MM-YYYY' },
    // DD/MM/YYYY (4 digit year)
    { pattern: /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/, day: 1, month: 2, year: 3, twoDigit: false, name: 'DD/MM/YYYY' },
    // YYYY-MM-DD
    { pattern: /^(\d{4})-(\d{1,2})-(\d{1,2})$/, day: 3, month: 2, year: 1, twoDigit: false, name: 'YYYY-MM-DD' },
    // YYYY/MM/DD
    { pattern: /^(\d{4})\/(\d{1,2})\/(\d{1,2})$/, day: 3, month: 2, year: 1, twoDigit: false, name: 'YYYY/MM/DD' }
  ];
  
  for (const format of formats) {
    const match = dateStr.match(format.pattern);
    if (match) {
      let day = parseInt(match[format.day]);
      let month = parseInt(match[format.month]);
      let year = parseInt(match[format.year]);
      
      // Handle 2-digit year with better logic
      if (format.twoDigit) {
        const currentYear = new Date().getFullYear();
        const currentCentury = Math.floor(currentYear / 100) * 100;
        
        // If year is > 50, assume 19xx, else 20xx
        if (year > 50) {
          year = 1900 + year;
        } else {
          year = 2000 + year;
        }
      }
      
      // Validate ranges
      if (day < 1 || day > 31 || month < 1 || month > 12 || year < 1900 || year > 2100) {
        console.log(`Date validation failed for ${format.name}:`, { day, month, year });
        continue;
      }
      
      // Create and validate date
      const parsedDate = new Date(year, month - 1, day);
      if (!isNaN(parsedDate.getTime()) && 
          parsedDate.getFullYear() === year && 
          parsedDate.getMonth() === month - 1 && 
          parsedDate.getDate() === day) {
        console.log(`Date parsing successful with ${format.name}:`, { 
          input: dateStr, 
          parsed: { day, month, year }, 
          result: parsedDate.toISOString().split('T')[0] 
        });
        return parsedDate.toISOString().split('T')[0];
      } else {
        console.log(`Date creation failed for ${format.name}:`, { day, month, year, parsedDate });
      }
    }
  }
  
  console.log('No date format matched for:', dateStr);
  return null;
};

// Detect date format from sample data
export const detectDateFormat = (sampleDates) => {
  if (!sampleDates || sampleDates.length === 0) return null;
  
  const formats = [
    { pattern: /^\d{1,2}-\d{1,2}-\d{4}$/, name: 'DD-MM-YYYY', example: '16-09-2024' },
    { pattern: /^\d{1,2}\/\d{1,2}\/\d{4}$/, name: 'DD/MM/YYYY', example: '16/09/2024' },
    { pattern: /^\d{1,2}-\d{1,2}-\d{2}$/, name: 'DD-MM-YY', example: '16-09-24' },
    { pattern: /^\d{1,2}\/\d{1,2}\/\d{2}$/, name: 'DD/MM/YY', example: '16/09/24' },
    { pattern: /^\d{4}-\d{1,2}-\d{1,2}$/, name: 'YYYY-MM-DD', example: '2024-09-16' },
    { pattern: /^\d{4}\/\d{1,2}\/\d{1,2}$/, name: 'YYYY/MM/DD', example: '2024/09/16' },
  ];
  
  // Count matches for each format
  const formatCounts = formats.map(format => ({
    ...format,
    matches: sampleDates.filter(date => format.pattern.test(String(date).trim())).length
  }));
  
  // Find the format with the most matches
  const bestFormat = formatCounts.reduce((best, current) => 
    current.matches > best.matches ? current : best
  );
  
  if (bestFormat.matches > 0) {
    return {
      format: bestFormat.name,
      example: bestFormat.example,
      confidence: bestFormat.matches / sampleDates.length,
      totalMatches: bestFormat.matches,
      totalSamples: sampleDates.length
    };
  }
  
  return null;
};

// Enhanced date parsing with fallback for mixed formats
// Parse date with fallback for common Excel issues - Fresh implementation
export const parseDateValueWithFallback = (value) => {
  if (!value) return null;
  
  // First try the main parsing function
  let result = parseDateValue(value);
  if (result) return result;
  
  // If that fails, try cleaning common Excel issues
  const cleanedValue = String(value)
    .replace(/\s+\d{2}:\d{2}:\d{2}.*$/, '') // Remove time portion
    .replace(/\.\d+$/, '') // Remove decimal part
    .trim();
  
  if (cleanedValue !== String(value)) {
    result = parseDateValue(cleanedValue);
    if (result) return result;
  }
  
  return null;
};

// Check for duplicates
export const findDuplicates = (data, existingData, keyField) => {
  return data.map(item => {
    const duplicate = existingData.find(existing => 
      existing[keyField] && 
      String(existing[keyField]).toLowerCase() === String(item[keyField]).toLowerCase()
    );
    
    return {
      ...item,
      isDuplicate: !!duplicate,
      duplicateId: duplicate?.id,
      existingItem: duplicate
    };
  });
};

// Show toast notifications
export const showToast = {
  loading: (message, id) => toast.loading(message, { id }),
  success: (message, id) => toast.success(message, { id }),
  error: (message, id) => toast.error(message, { id }),
  dismiss: (id) => toast.dismiss(id)
};

// Common field definitions for different modules
export const FIELD_DEFINITIONS = {
  parties: [
    { key: 'firmName', label: 'Firm/Company Name', required: true, description: 'Name of the party/company', autoMapPatterns: ['firm', 'company', 'name', 'party'] },
    { key: 'personName', label: 'Contact Person', required: false, description: 'Name of the contact person', autoMapPatterns: ['person', 'contact', 'name'] },
    { key: 'contact', label: 'Phone Number', required: false, description: 'Primary phone number', autoMapPatterns: ['phone', 'contact', 'mobile', 'number'] },
    { key: 'email', label: 'Email Address', required: false, description: 'Email address', autoMapPatterns: ['email', 'mail'] },
    { key: 'whatsapp', label: 'WhatsApp Number', required: false, description: 'WhatsApp number', autoMapPatterns: ['whatsapp', 'wa'] },
    { key: 'address', label: 'Address', required: false, description: 'Complete address', autoMapPatterns: ['address', 'location'] },
    { key: 'gstin', label: 'GSTIN', required: false, description: 'GST registration number', autoMapPatterns: ['gstin', 'gst'] },
    { key: 'gstType', label: 'GST Type', required: false, description: 'Type of GST registration', autoMapPatterns: ['gst_type', 'gsttype'] },
    { key: 'partyType', label: 'Party Type', required: false, description: 'Buyer or Seller', autoMapPatterns: ['type', 'party_type', 'partytype'] },
    { key: 'openingBalance', label: 'Opening Balance', required: false, description: 'Opening balance amount', autoMapPatterns: ['balance', 'opening', 'amount'] }
  ],
  
  sales: [
    { key: 'invoiceNumber', label: 'Invoice Number', required: false, description: 'Unique invoice number (auto-generated if empty)', autoMapPatterns: ['invoice', 'number', 'bill'] },
    { key: 'customerName', label: 'Customer/Party Name', required: true, description: 'Name of the customer/party', autoMapPatterns: ['customer', 'client', 'name', 'party', 'work_name'] },
    { key: 'date', label: 'Invoice Date', required: true, description: 'Date of the invoice', autoMapPatterns: ['date', 'invoice_date'] },
    { key: 'dueDate', label: 'Due Date', required: false, description: 'Payment due date', autoMapPatterns: ['due', 'due_date', 'duedate'] },
    { key: 'workType', label: 'Work Type/Item Name', required: true, description: 'Type of work or item name', autoMapPatterns: ['work_type', 'item', 'product', 'service'] },
    { key: 'nos', label: 'Nos (Quantity)', required: false, description: 'Number of items', autoMapPatterns: ['nos', 'qty', 'quantity', 'count'] },
    { key: 'length', label: 'Length (M1)', required: false, description: 'First measurement dimension', autoMapPatterns: ['length', 'l', 'm1'] },
    { key: 'height', label: 'Height (M2)', required: false, description: 'Second measurement dimension', autoMapPatterns: ['height', 'h', 'm2'] },
    { key: 'width', label: 'Width (M3)', required: false, description: 'Third measurement dimension', autoMapPatterns: ['width', 'w', 'm3'] },
    { key: 'depth', label: 'Depth (M4)', required: false, description: 'Fourth measurement dimension', autoMapPatterns: ['depth', 'd', 'm4'] },
    { key: 'area', label: 'Area/Total Qty', required: true, description: 'Calculated area or total quantity', autoMapPatterns: ['area', 'qty', 'quantity', 'total_qty'] },
    { key: 'rate', label: 'Rate per Unit', required: true, description: 'Rate per unit', autoMapPatterns: ['rate', 'price', 'unit_rate'] },
    { key: 'amount', label: 'Total Amount', required: true, description: 'Total amount for this line', autoMapPatterns: ['amount', 'total', 'line_total'] },
    { key: 'totalAmount', label: 'Invoice Total', required: false, description: 'Total invoice amount', autoMapPatterns: ['total_amount', 'invoice_total', 'grand_total'] },
    { key: 'taxAmount', label: 'Tax Amount', required: false, description: 'Total tax amount', autoMapPatterns: ['tax', 'gst', 'vat'] },
    { key: 'discount', label: 'Discount', required: false, description: 'Discount amount', autoMapPatterns: ['discount', 'off'] },
    { key: 'paymentStatus', label: 'Payment Status', required: false, description: 'Payment status from remarks', autoMapPatterns: ['status', 'payment', 'paid', 'remark'] },
    { key: 'notes', label: 'Notes/Remarks', required: false, description: 'Additional notes or remarks', autoMapPatterns: ['notes', 'remarks', 'comment', 'remark'] }
  ],
  
  purchases: [
    { key: 'billNumber', label: 'Bill Number', required: true, description: 'Purchase bill number', autoMapPatterns: ['bill', 'number', 'purchase'] },
    { key: 'supplierName', label: 'Supplier Name', required: true, description: 'Name of the supplier', autoMapPatterns: ['supplier', 'vendor', 'name', 'party'] },
    { key: 'date', label: 'Bill Date', required: true, description: 'Date of the purchase bill', autoMapPatterns: ['date', 'bill_date'] },
    { key: 'dueDate', label: 'Due Date', required: false, description: 'Payment due date', autoMapPatterns: ['due', 'due_date', 'duedate'] },
    { key: 'totalAmount', label: 'Total Amount', required: true, description: 'Total bill amount', autoMapPatterns: ['total', 'amount', 'sum'] },
    { key: 'taxAmount', label: 'Tax Amount', required: false, description: 'Total tax amount', autoMapPatterns: ['tax', 'gst', 'vat'] },
    { key: 'discount', label: 'Discount', required: false, description: 'Discount amount', autoMapPatterns: ['discount', 'off'] },
    { key: 'paymentStatus', label: 'Payment Status', required: false, description: 'Payment status', autoMapPatterns: ['status', 'payment', 'paid'] },
    { key: 'notes', label: 'Notes', required: false, description: 'Additional notes', autoMapPatterns: ['notes', 'remarks', 'comment'] }
  ],
  
  expenses: [
    { key: 'head', label: 'Expense Head', required: true, description: 'Category/head of expense', autoMapPatterns: ['head', 'category', 'type', 'expense'] },
    { key: 'amount', label: 'Amount', required: true, description: 'Expense amount', autoMapPatterns: ['amount', 'cost', 'price', 'value'] },
    { key: 'date', label: 'Date', required: true, description: 'Date of expense', autoMapPatterns: ['date', 'expense_date'] },
    { key: 'description', label: 'Description', required: false, description: 'Description of expense', autoMapPatterns: ['description', 'details', 'remarks'] },
    { key: 'paymentMode', label: 'Payment Mode', required: false, description: 'Payment method', autoMapPatterns: ['payment', 'mode', 'method'] },
    { key: 'group', label: 'Group', required: false, description: 'Expense group', autoMapPatterns: ['group', 'category'] },
    { key: 'employeeName', label: 'Employee Name', required: false, description: 'Name of employee', autoMapPatterns: ['employee', 'staff', 'name'] },
    { key: 'receiptNumber', label: 'Receipt Number', required: false, description: 'Receipt/bill number', autoMapPatterns: ['receipt', 'bill', 'number'] },
    { key: 'notes', label: 'Notes', required: false, description: 'Additional notes', autoMapPatterns: ['notes', 'remarks', 'comment'] }
  ]
};
